import { AccountInfo, InteractionRequiredAuthError } from "@azure/msal-browser";
import axios, {
  AxiosHeaders,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import ConfigService from "./ConfigService";
import { STORAGE_KEYS } from "../constants/storageKeys";
import {
  generateUserContextCustomHeader,
  getSessionStorageItem,
} from "../utils/utils";
import { USER_CONTEXT_CUSTOM_HEADER } from "../constants/appConstants";

interface TokenRequest {
  scopes: string[];
  account: AccountInfo;
}

const getAxiosDefaultCreateObj = (
  baseVersionUrl: string,
  _apiVersion = "v0.1/",
) => {
  return {
    baseURL: `${ConfigService.getEnvValue(baseVersionUrl)}`,
    headers: {
      "Content-type": "application/json",
    },
  };
};

export const apiClient: AxiosInstance = axios.create(
  getAxiosDefaultCreateObj("REACT_APP_BASE_URL"),
);

const clientConfig = (config: InternalAxiosRequestConfig) => {
  const accessTokenRequest: TokenRequest = {} as TokenRequest;

  const authConfig = ConfigService.getMsalInstance();
  return authConfig.then((data) => {
    accessTokenRequest.scopes = data.config.auth.scopes;

    const msalInstance = data.msalInstance;
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return config;

    accessTokenRequest.account = accounts[0]!;

    return msalInstance
      .acquireTokenSilent(accessTokenRequest)
      .then((accessTokenResponse) => {
        const accessToken = accessTokenResponse.accessToken;

        if (config.headers) {
          const headers = config.headers as AxiosHeaders;
          headers.set("Authorization", `Bearer ${accessToken}`);

          const userContextCustomHeaderResponse =
            generateUserContextCustomHeader();
          if (userContextCustomHeaderResponse.isUserPracticeInfoTruthy) {
            headers.set(
              USER_CONTEXT_CUSTOM_HEADER,
              userContextCustomHeaderResponse.userContextCustomHeader,
            );
          }

          headers.set(
            "custom-authorization",
            getSessionStorageItem(STORAGE_KEYS.UserContextToken),
          );
        }

        return Promise.resolve(config);
      })
      .catch(async (e) => {
        if (e instanceof InteractionRequiredAuthError) {
          await msalInstance.acquireTokenRedirect(accessTokenRequest);
        }

        throw e;
      });
  });
};

const errorHandling = (error: unknown) => {
  return Promise.reject(error);
};

// Utility function to get the current access token
export const getCurrentAccessToken = async (): Promise<string | null> => {
  try {
    const authConfig = await ConfigService.getMsalInstance();
    const msalInstance = authConfig.msalInstance;
    const accounts = msalInstance.getAllAccounts();

    if (accounts.length === 0) return null;

    const accessTokenRequest: TokenRequest = {
      scopes: authConfig.config.auth.scopes,
      account: accounts[0]!,
    };

    const accessTokenResponse =
      await msalInstance.acquireTokenSilent(accessTokenRequest);
    return accessTokenResponse.accessToken;
  } catch (error) {
    console.error("Error getting access token:", error);
    return null;
  }
};

apiClient.interceptors.request.use(
  function (config) {
    return clientConfig(config);
  },
  function (error) {
    return errorHandling(error);
  },
);

// Response interceptor to handle 204 No Content responses
apiClient.interceptors.response.use(
  function (response) {
    // Handle 204 No Content responses
    if (response.status === 204) {
      return { ...response, data: undefined };
    }
    return response;
  },
  function (error) {
    return errorHandling(error);
  },
);
